import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  selectActiveProjects,
  selectActiveProject,
  selectBasemap,
  selectAllProjectsInActiveFeature,
  selectActiveFeature,
  selectSidebarIsOpen,
  selectSidebarScrollup,
  selectAllRegionContent,
  selectRoundFilter,
} from "../../reducers";
import PropTypes from "prop-types";
import {
  setSidebarIsOpen,
  setZoomToNsw,
  setActiveFeature,
  setActiveProject,
  setActiveFeatureByName,
} from "../../actions/ui";
import styles from "./sidebar.module.scss";
import ProjectCard from "../projectCard";
import { colors } from "../../modules/theme";
import { ChevronDoubleLeft, ChevronDoubleRight } from "../icons/icons";
import { projectShape } from "../../modules/project";

const Sidebar = (props) => {
  const {
    activeProjects,
    activeProject,
    filteredProjectsInActiveFeature,
    activeFeature,
    sidebarIsOpen,
    setSidebarIsOpen,
    sideBarScroll,
    allRegionContent,
    setZoomToNsw,
    setActiveFeature,
    setActiveProject,
    setActiveFeatureByName,
    roundFilter,
  } = props;
  //eslint-disable-next-line
  const [_viewingProjectIndex, setViewingProjectIndex] = useState(0);

  const scrollContainer = useRef(null);

  useEffect(() => {
    setViewingProjectIndex(0);
  }, [activeProjects]);

  // Scroll to the top of the sidebar when something is selected
  useEffect(() => {
    scrollContainer.current.scrollTop = 0;
  }, [sideBarScroll]);

  const regionContent = activeFeature
    ? allRegionContent[activeFeature.properties.REGION]
    : null;

  const title =
    activeProjects.length === 0 && !activeFeature
      ? "Our Regions"
      : activeFeature.properties.REGION;
  const activeProjectObject = activeProject
    ? filteredProjectsInActiveFeature.find((d) => d.project === activeProject)
    : null;
  const nonActiveProjects = filteredProjectsInActiveFeature
    .filter((d) => d.project !== activeProject)
    .filter((d) =>
      roundFilter ? roundFilter.split(",").includes(d.round.toString()) : true
    )
    .sort((a, b) => a.project.localeCompare(b.project));

  return (
    <div
      className={`${styles.sidebarContainer} ${
        sidebarIsOpen ? styles.sidebarOpen : styles.sidebarClosed
      }`}
      onClick={() => {
        setSidebarIsOpen(!sidebarIsOpen);
      }}
      id="goregional-sidebar"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={styles.projectsSidebar}
        ref={scrollContainer}
        style={{
          backgroundColor: "#fff",
        }}
      >
        {activeFeature && (
          <button
            className={styles.sidebarBackButton}
            onClick={() => {
              setActiveFeature(null);
              setActiveProject(null);
              setZoomToNsw(Date.now());
            }}
          >
            &laquo; Back to NSW / All Regions
          </button>
        )}
        <h3
          className={styles.sidebarSectionHeader}
          style={{
            color: colors.sectionHeader,
            marginTop: 0,
          }}
        >
          {title}
        </h3>

        {activeFeature && (
          <div className={styles.regionHeader}>
            <figure>
              <img
                width="100%"
                src={regionContent.image}
                alt={
                  "Image for " +
                  activeFeature.properties.REGION +
                  " from " +
                  regionContent.image_ref
                }
              />
              <figcaption>
                {"Image credit: " + regionContent.image_ref}
              </figcaption>
            </figure>
          </div>
        )}

        {!activeFeature && allRegionContent && (
          <div className={styles.allRegions}>
            <h4>Find out more about projects by selecting a region below</h4>
            <ul className={styles.allRegionsList}>
              {Object.entries(allRegionContent).map((r, key) => {
                return (
                  <li
                    key={key + r[0]}
                    tabIndex={sidebarIsOpen ? 0 : -1}
                    onClick={() => {
                      setActiveFeatureByName(r[0]);
                      setActiveProject(null);
                    }}
                  >
                    {r[0]}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/*Active project*/}
        {activeProject && <h3>Selected project</h3>}

        {activeProject && (
          <div>
            {activeProjectObject && (
              <ProjectCard
                tabIndex={sidebarIsOpen ? 0 : -1}
                project={activeProjectObject}
                key={"active-" + activeProjectObject.link}
                activeCard={true}
              ></ProjectCard>
            )}
          </div>
        )}

        {/*List of projects*/}
        {activeFeature && (
          <h3>
            {nonActiveProjects.length > 0
              ? "Projects (" + nonActiveProjects.length + ")"
              : ""}
          </h3>
        )}

        {activeFeature && nonActiveProjects.length > 0 && (
          <div>
            {!activeFeature && activeProjects.length > 0 && (
              <h3
                className={styles.sidebarSectionHeader}
                style={{
                  color: colors.sectionHeader,
                }}
              >
                More in NSW
              </h3>
            )}
            {nonActiveProjects.map((p, key) => {
              return (
                <ProjectCard
                  tabIndex={sidebarIsOpen ? 0 : -1}
                  project={p}
                  key={key + p.link}
                  activeCard={false}
                ></ProjectCard>
              );
            })}
          </div>
        )}

        {/*Region info*/}
        {activeFeature && (
          <div className={styles.regionContent}>
            <h3>About this region</h3>
            {regionContent.description}
          </div>
        )}
      </div>
      <div
        className={styles.sidebarHandle}
        style={{ backgroundColor: "rbga(255, 255, 255, 0.5)" }}
      >
        <button className={styles.sidebarHandleButton}>
          {sidebarIsOpen ? (
            <ChevronDoubleLeft color="#000" width="14px" height="14px" />
          ) : (
            <ChevronDoubleRight color="#000" width="14px" height="14px" />
          )}
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (s) => ({
  activeProjects: selectActiveProjects(s),
  activeProject: selectActiveProject(s),
  basemap: selectBasemap(s),
  filteredProjectsInActiveFeature: selectAllProjectsInActiveFeature(s),
  activeFeature: selectActiveFeature(s),
  sidebarIsOpen: selectSidebarIsOpen(s),
  sideBarScroll: selectSidebarScrollup(s),
  allRegionContent: selectAllRegionContent(s),
  roundFilter: selectRoundFilter(s),
});
const mapDispatchToProps = {
  setSidebarIsOpen,
  setZoomToNsw,
  setActiveFeature,
  setActiveProject,
  setActiveFeatureByName,
};
Sidebar.propTypes = {
  setSidebarIsOpen: PropTypes.func.isRequired,
  activeProjects: PropTypes.array.isRequired,
  activeProject: PropTypes.string,
  activeFeature: PropTypes.object,
  filteredProjectsInActiveFeature: PropTypes.arrayOf(
    PropTypes.shape(projectShape)
  ),
  setActiveFeatureByName: PropTypes.func.isRequired,
  sidebarIsOpen: PropTypes.bool,
  sideBarScroll: PropTypes.number,
  allRegionContent: PropTypes.object,
  setZoomToNsw: PropTypes.func.isRequired,
  setActiveFeature: PropTypes.func.isRequired,
  setActiveProject: PropTypes.func.isRequired,
  roundFilter: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);



// WEBPACK FOOTER //
// ./src/components/sidebar/sidebar.js